<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import {
  instanceColor,
  ensureKeypoint,
  type EditorInstance,
} from '@/helpers/LabelingSerialization';
import { rasterizePolygon } from '@/helpers/MaskAnalysis';
import { maskHasPixels } from '@/helpers/MaskToPolygon';
import { POINT_HIT_RADIUS } from '@/helpers/RobvisionConstants';

export type LabelingToolId =
  | 'sam'
  | 'brush'
  | 'erase'
  | 'polygon'
  | 'keypoint'
  | 'rotate'
  | 'pan';

export interface PromptPoint {
  point: [number, number]; // normalized 0..1 in image space
  label: 0 | 1;
}

const props = defineProps<{
  imageElement: HTMLImageElement | null;
  instances: EditorInstance[];
  selectedId: number | null;
  tool: LabelingToolId;
  promptMode: 'positive' | 'negative';
  brushSize: number;
  rotationEnabled: boolean;
  samReady: boolean;
  readOnly: boolean;
  // Returns the selected instance, creating a fresh one when the list is
  // empty — the parent owns instance ids and selection.
  ensureInstance: () => EditorInstance | null;
}>();

const emit = defineEmits<{
  // Fired before the canvas mutates the selected instance's mask so the
  // parent can push an undo snapshot.
  'before-edit': [];
  edited: [];
  'request-mask': [points: PromptPoint[]];
  // Hover preview in AI mode: current prompt points + the hover position.
  'request-preview': [points: PromptPoint[]];
  'sam-not-ready': [];
  'keypoint-set': [point: [number, number]];
  'select-instance': [id: number];
  'zoom-changed': [zoom: number];
  'polygon-count-changed': [count: number];
}>();

const canvasRef = ref<HTMLCanvasElement | null>(null);
const containerRef = ref<HTMLDivElement | null>(null);

const zoom = ref(1);
let pan = { x: 0, y: 0 };
let activeDrag:
  | {
      kind: 'pan';
      startX: number;
      startY: number;
      downX: number;
      downY: number;
    }
  | { kind: 'draw' }
  | { kind: 'rotate' }
  | null = null;
let lastDrawPos: { x: number; y: number } | null = null;
let strokeStarted = false;

const samPoints = ref<PromptPoint[]>([]);
const hoveredPointIndex = ref(-1);
// Tinted raster of the mask a click at the hover position would produce.
let previewCanvas: HTMLCanvasElement | null = null;
let lastPreviewAt = 0;
let lastPreviewPos: { x: number; y: number } | null = null;
const PREVIEW_THROTTLE_MS = 150;
const PREVIEW_FALLBACK_COLOR = '#83DED9';
const polygonPoints = ref<Array<[number, number]>>([]);
const polygonPreview = ref<[number, number] | null>(null);
const hoveredPolygonVertex = ref(-1);
let brushCursor: { x: number; y: number } | null = null;

// Cached opaque overlay per instance (per-instance alpha is applied at blit
// time); rebuilt lazily when marked dirty, brush strokes stamp onto it
// incrementally.
const overlayCache = new Map<
  number,
  {
    canvas: HTMLCanvasElement;
    dirty: boolean;
    // Topmost mask pixel — anchor for the on-canvas name label.
    anchor: [number, number] | null;
    // Mask bounding box [minX, minY, maxX, maxY] — sizes the rotation arrow.
    bbox: [number, number, number, number] | null;
  }
>();

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  resizeObserver = new ResizeObserver(() => {
    syncCanvasSize();
    redraw();
  });
  if (containerRef.value) {
    resizeObserver.observe(containerRef.value);
  }
  syncCanvasSize();
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

function syncCanvasSize() {
  if (!canvasRef.value || !containerRef.value) {
    return;
  }
  canvasRef.value.width = containerRef.value.clientWidth;
  canvasRef.value.height = containerRef.value.clientHeight;
}

watch(
  () => props.imageElement,
  () => {
    resetSession();
    zoomToFit();
  },
);

watch(
  () => props.selectedId,
  (_selected, previous) => {
    // Prompt points and polygon drafts belong to one instance. A change
    // away from "nothing selected" is the lazy creation for an in-flight
    // gesture — its fresh draft must survive.
    if (previous !== null) {
      clearDrafts();
    }
    markAllDirty();
    redraw();
  },
);

watch(
  () => props.tool,
  (tool, prevTool) => {
    if (prevTool === 'polygon' && polygonPoints.value.length >= 3) {
      commitPolygon();
    }
    if (tool !== 'sam') {
      samPoints.value = [];
      previewCanvas = null;
    }
    redraw();
  },
);

watch(
  () => props.instances.length,
  () => {
    for (const id of overlayCache.keys()) {
      if (!props.instances.some((instance) => instance.id === id)) {
        overlayCache.delete(id);
      }
    }
    redraw();
  },
);

function clearDrafts() {
  samPoints.value = [];
  polygonPoints.value = [];
  polygonPreview.value = null;
  hoveredPolygonVertex.value = -1;
  previewCanvas = null;
  emit('polygon-count-changed', 0);
}

/** Escape: abandon the in-progress polygon draft / SAM prompt points
 * without committing anything. Returns whether there was one to abandon. */
function cancelDrafts(): boolean {
  const hadDraft = samPoints.value.length > 0 || polygonPoints.value.length > 0;
  clearDrafts();
  redraw();
  return hadDraft;
}

function resetSession() {
  clearDrafts();
  overlayCache.clear();
  activeDrag = null;
  lastDrawPos = null;
  lastPreviewPos = null;
}

const selectedInstance = () =>
  props.instances.find((instance) => instance.id === props.selectedId) ?? null;

/** Edit target for the mutating tools. With create, an empty list gets a
 * fresh object from the parent. The parent's callback is also the fallback
 * while `props.selectedId` is still catching up on the next render after a
 * lazy creation mid-gesture. */
function activeInstance(create: boolean): EditorInstance | null {
  const existing = selectedInstance();
  if (existing || !create || props.readOnly) {
    return existing;
  }
  return props.ensureInstance();
}

// ---- coordinate helpers ----

function getMousePos(e: MouseEvent) {
  if (!canvasRef.value) {
    return null;
  }
  const rect = canvasRef.value.getBoundingClientRect();
  return {
    x: (e.clientX - rect.left - pan.x) / zoom.value,
    y: (e.clientY - rect.top - pan.y) / zoom.value,
  };
}

// ---- zoom / pan ----

function setZoom(next: number, anchor?: { x: number; y: number }) {
  const clamped = Math.min(8, Math.max(0.1, next));
  if (anchor && canvasRef.value) {
    // Keep the anchor point (canvas coords) fixed on screen.
    pan = {
      x: anchor.x - ((anchor.x - pan.x) / zoom.value) * clamped,
      y: anchor.y - ((anchor.y - pan.y) / zoom.value) * clamped,
    };
  }
  zoom.value = clamped;
  emit('zoom-changed', clamped);
  redraw();
}

function zoomIn() {
  centerAnchoredZoom(zoom.value * 1.2);
}

function zoomOut() {
  centerAnchoredZoom(zoom.value / 1.2);
}

function centerAnchoredZoom(next: number) {
  if (!canvasRef.value) {
    return;
  }
  setZoom(next, {
    x: canvasRef.value.width / 2,
    y: canvasRef.value.height / 2,
  });
}

function zoomToFit() {
  if (!canvasRef.value || !props.imageElement) {
    return;
  }
  syncCanvasSize();
  const cw = canvasRef.value.width;
  const ch = canvasRef.value.height;
  // A not-yet-laid-out container would produce a negative scale (mirrored,
  // invisible image); the imageElement watcher retriggers once layout runs.
  if (cw <= 24 || ch <= 24) {
    return;
  }
  const iw = props.imageElement.width;
  const ih = props.imageElement.height;
  const scale = Math.min((cw - 24) / iw, (ch - 24) / ih);
  zoom.value = Math.min(Math.max(scale, 0.1), 4);
  pan = {
    x: (cw - iw * zoom.value) / 2,
    y: (ch - ih * zoom.value) / 2,
  };
  emit('zoom-changed', zoom.value);
  redraw();
}

function handleWheel(e: WheelEvent) {
  e.preventDefault();
  if (!canvasRef.value) {
    return;
  }
  const rect = canvasRef.value.getBoundingClientRect();
  const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
  setZoom(zoom.value * factor, {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top,
  });
}

// ---- mask edits ----

function ensureStroke() {
  if (!strokeStarted) {
    strokeStarted = true;
    emit('before-edit');
  }
}

// The exact pixel disk the mask loop writes, pre-rendered opaque once per
// (radius, color). An antialiased arc is NOT that shape — rim pixels differ
// and the fringe sharpens on the stroke-end rebuild, which showed as the
// mask visibly changing on mouseup.
let stampCache: {
  radius: number;
  color: string;
  canvas: HTMLCanvasElement;
} | null = null;

function brushStamp(radius: number, color: string): HTMLCanvasElement | null {
  if (
    stampCache &&
    stampCache.radius === radius &&
    stampCache.color === color
  ) {
    return stampCache.canvas;
  }
  const size = radius * 2 + 1;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  const imageData = ctx.createImageData(size, size);
  const out = imageData.data;
  const [r, g, b] = hexToRgb(color);
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      if (dx * dx + dy * dy <= radius * radius) {
        const o = ((dy + radius) * size + (dx + radius)) * 4;
        out[o] = r;
        out[o + 1] = g;
        out[o + 2] = b;
        out[o + 3] = 255;
      }
    }
  }
  ctx.putImageData(imageData, 0, 0);
  stampCache = { radius, color, canvas };
  return canvas;
}

function drawAtPosition(x: number, y: number) {
  const instance = activeInstance(true);
  if (!instance || !props.imageElement) {
    return;
  }
  const { width, height } = props.imageElement;
  const { mask } = instance;
  const px = Math.round(x);
  const py = Math.round(y);
  const radius = Math.max(1, Math.round(props.brushSize / zoom.value));
  const erasing = props.tool === 'erase';
  const value = erasing ? 0 : 255;
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      if (dx * dx + dy * dy <= radius * radius) {
        const nx = px + dx;
        const ny = py + dy;
        if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
          mask[ny * width + nx] = value;
        }
      }
    }
  }
  if (!erasing) {
    instance.hasMask = true;
  }
  // Stamp the overlay incrementally — a full rebuild per mousemove
  // (width × height ImageData) makes the brush lag behind the cursor. The
  // stroke-end resync in handleMouseUp restores pixel-exact state.
  const overlay = overlayFor(instance);
  const overlayCtx = overlay?.getContext('2d');
  if (overlayCtx) {
    // Stamp the identical pixel disk the mask loop just wrote — integer
    // offset, no antialiasing — so the stroke-end resync repaints the very
    // same pixels and nothing visibly changes on mouseup.
    const stamp = brushStamp(radius, instanceColor(instance.id));
    if (stamp) {
      overlayCtx.globalCompositeOperation = erasing
        ? 'destination-out'
        : 'source-over';
      overlayCtx.drawImage(stamp, px - radius, py - radius);
      overlayCtx.globalCompositeOperation = 'source-over';
    }
  }
}

function commitPolygon() {
  if (!props.imageElement || polygonPoints.value.length < 3) {
    return;
  }
  // Snapshot before the lazy creation, so undo removes the new object too.
  emit('before-edit');
  const instance = activeInstance(true);
  if (!instance) {
    return;
  }
  rasterizePolygon(
    polygonPoints.value,
    instance.mask,
    props.imageElement.width,
    props.imageElement.height,
  );
  instance.hasMask = true;
  ensureKeypoint(instance, props.imageElement.width, props.imageElement.height);
  polygonPoints.value = [];
  polygonPreview.value = null;
  hoveredPolygonVertex.value = -1;
  emit('polygon-count-changed', 0);
  markDirty(instance.id);
  redraw();
  emit('edited');
}

function polygonVertexHitTest(x: number, y: number): number {
  const radius = POINT_HIT_RADIUS / zoom.value;
  for (let i = 0; i < polygonPoints.value.length; i++) {
    const [px, py] = polygonPoints.value[i];
    if (Math.hypot(px - x, py - y) <= radius) {
      return i;
    }
  }
  return -1;
}

function samPointHitTest(x: number, y: number): number {
  if (!props.imageElement) {
    return -1;
  }
  const { width, height } = props.imageElement;
  const radius = POINT_HIT_RADIUS / zoom.value;
  for (let i = samPoints.value.length - 1; i >= 0; i--) {
    const p = samPoints.value[i];
    if (Math.hypot(p.point[0] * width - x, p.point[1] * height - y) <= radius) {
      return i;
    }
  }
  return -1;
}

function handleSamClick(pos: { x: number; y: number }, forceNegative: boolean) {
  if (props.readOnly || !props.imageElement) {
    return;
  }
  if (!props.samReady) {
    // Clicks before the embedding is ready look like the tool is broken —
    // let the parent explain instead of swallowing them.
    emit('sam-not-ready');
    return;
  }
  if (!activeInstance(true)) {
    return;
  }
  const hit = samPointHitTest(pos.x, pos.y);
  if (hit >= 0) {
    samPoints.value.splice(hit, 1);
  } else {
    const nx = pos.x / props.imageElement.width;
    const ny = pos.y / props.imageElement.height;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) {
      return;
    }
    samPoints.value.push({
      point: [nx, ny],
      label: forceNegative || props.promptMode === 'negative' ? 0 : 1,
    });
  }
  if (samPoints.value.length > 0) {
    emit(
      'request-mask',
      samPoints.value.map((p) => ({
        point: [p.point[0], p.point[1]] as [number, number],
        label: p.label,
      })),
    );
  }
  redraw();
}

/** Parent hands back the decoded hover mask; null clears the preview. */
function setPreviewMask(mask: Uint8Array | null) {
  if (!mask || !props.imageElement) {
    previewCanvas = null;
    redraw();
    return;
  }
  const { width, height } = props.imageElement;
  const offscreen = document.createElement('canvas');
  offscreen.width = width;
  offscreen.height = height;
  const ctx = offscreen.getContext('2d');
  if (!ctx) {
    return;
  }
  // Tint in the target object's color — the preview shows what the click
  // would add to it.
  const [r, g, b] = hexToRgb(
    props.selectedId !== null
      ? instanceColor(props.selectedId)
      : PREVIEW_FALLBACK_COLOR,
  );
  const imageData = ctx.createImageData(width, height);
  const out = imageData.data;
  for (let i = 0; i < width * height; i++) {
    if (mask[i] > 0) {
      const o = i * 4;
      out[o] = r;
      out[o + 1] = g;
      out[o + 2] = b;
      out[o + 3] = 255;
    }
  }
  ctx.putImageData(imageData, 0, 0);
  previewCanvas = offscreen;
  redraw();
}

function maybeRequestPreview(pos: { x: number; y: number }) {
  if (!props.samReady || props.readOnly || !props.imageElement) {
    return;
  }
  const { width, height } = props.imageElement;
  if (pos.x < 0 || pos.x >= width || pos.y < 0 || pos.y >= height) {
    if (previewCanvas) {
      setPreviewMask(null);
    }
    return;
  }
  const now = Date.now();
  const minMove = 4 / zoom.value; // ~4 screen px
  if (
    now - lastPreviewAt < PREVIEW_THROTTLE_MS ||
    (lastPreviewPos &&
      Math.hypot(pos.x - lastPreviewPos.x, pos.y - lastPreviewPos.y) < minMove)
  ) {
    return;
  }
  lastPreviewAt = now;
  lastPreviewPos = pos;
  emit('request-preview', [
    ...samPoints.value.map((p) => ({
      point: [p.point[0], p.point[1]] as [number, number],
      label: p.label,
    })),
    { point: [pos.x / width, pos.y / height], label: 1 },
  ]);
}

function instanceAt(x: number, y: number): number | null {
  if (!props.imageElement) {
    return null;
  }
  const { width, height } = props.imageElement;
  const px = Math.round(x);
  const py = Math.round(y);
  if (px < 0 || py < 0 || px >= width || py >= height) {
    return null;
  }
  for (let i = props.instances.length - 1; i >= 0; i--) {
    if (props.instances[i].mask[py * width + px] > 0) {
      return props.instances[i].id;
    }
  }
  return null;
}

// ---- pointer handlers ----

function handleMouseDown(e: MouseEvent) {
  if (!props.imageElement) {
    return;
  }
  if (
    e.button === 1 ||
    (e.button === 0 && e.ctrlKey) ||
    (e.button === 0 && props.tool === 'pan') ||
    // Ready images are read-only until explicitly reopened; every drag
    // pans so the labels can still be inspected.
    (e.button === 0 && props.readOnly)
  ) {
    e.preventDefault();
    activeDrag = {
      kind: 'pan',
      startX: e.clientX - pan.x,
      startY: e.clientY - pan.y,
      downX: e.clientX,
      downY: e.clientY,
    };
    return;
  }
  if (e.button !== 0) {
    return;
  }
  if (props.tool === 'brush' || props.tool === 'erase') {
    const pos = getMousePos(e);
    if (!pos) {
      return;
    }
    // Snapshot first: drawAtPosition may lazily create the target object,
    // and undo must remove it again.
    ensureStroke();
    activeDrag = { kind: 'draw' };
    drawAtPosition(pos.x, pos.y);
    lastDrawPos = pos;
    redraw();
    return;
  }
  if (props.tool === 'rotate') {
    const pos = getMousePos(e);
    const instance = selectedInstance();
    if (!pos || !instance?.keypoint) {
      return;
    }
    emit('before-edit');
    activeDrag = { kind: 'rotate' };
    rotateTowards(instance, pos);
  }
}

/** Point the selected object's rotation arrow at the cursor. */
function rotateTowards(
  instance: EditorInstance,
  pos: { x: number; y: number },
) {
  const [kx, ky] = instance.keypoint!;
  const dx = pos.x - kx;
  const dy = pos.y - ky;
  // On the pick-point itself the direction is undefined — keep the angle.
  if (Math.hypot(dx, dy) < 1) {
    return;
  }
  // Zero points along the image y-axis (up), matching the draw convention.
  instance.angleDeg = Math.round((Math.atan2(dx, -dy) * 180) / Math.PI);
  instance.touched = true;
  redraw();
}

function handleMouseMove(e: MouseEvent) {
  if (activeDrag?.kind === 'pan') {
    pan = {
      x: e.clientX - activeDrag.startX,
      y: e.clientY - activeDrag.startY,
    };
    redraw();
    return;
  }
  const pos = getMousePos(e);
  if (!pos) {
    return;
  }

  if (activeDrag?.kind === 'rotate') {
    const instance = selectedInstance();
    if (instance?.keypoint) {
      rotateTowards(instance, pos);
    }
    return;
  }

  if (activeDrag?.kind === 'draw') {
    if (lastDrawPos) {
      const dx = pos.x - lastDrawPos.x;
      const dy = pos.y - lastDrawPos.y;
      const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy)));
      for (let i = 0; i <= steps; i++) {
        drawAtPosition(
          lastDrawPos.x + (dx * i) / steps,
          lastDrawPos.y + (dy * i) / steps,
        );
      }
    } else {
      drawAtPosition(pos.x, pos.y);
    }
    lastDrawPos = pos;
    brushCursor = pos;
    redraw();
    return;
  }

  let needsRedraw = false;
  if ((props.tool === 'brush' || props.tool === 'erase') && !props.readOnly) {
    brushCursor = pos;
    needsRedraw = true;
  } else if (brushCursor) {
    brushCursor = null;
    needsRedraw = true;
  }

  if (props.tool === 'sam') {
    const idx = samPointHitTest(pos.x, pos.y);
    if (idx !== hoveredPointIndex.value) {
      hoveredPointIndex.value = idx;
      needsRedraw = true;
    }
    maybeRequestPreview(pos);
  }

  if (props.tool === 'polygon') {
    const idx = polygonVertexHitTest(pos.x, pos.y);
    if (idx !== hoveredPolygonVertex.value) {
      hoveredPolygonVertex.value = idx;
      needsRedraw = true;
    }
    // Keep the dashed preview while hovering a vertex; snap it onto the
    // hovered vertex so closing on the first point reads as "will close".
    polygonPreview.value =
      polygonPoints.value.length >= 1
        ? idx >= 0
          ? [...polygonPoints.value[idx]]
          : [pos.x, pos.y]
        : null;
    needsRedraw = true;
  }

  if (needsRedraw) {
    redraw();
  }
}

function handleMouseUp(e: MouseEvent) {
  const wasDrag = activeDrag;
  activeDrag = null;
  lastDrawPos = null;

  if (wasDrag?.kind === 'draw') {
    strokeStarted = false;
    // Resync the overlay from the mask once per stroke: stamps outside the
    // image are clipped by the canvas while the mask loop bounds-checks, and
    // the cached bbox/anchor must catch up. An erase stroke may have
    // removed the last pixel, so the reactive flag is recomputed.
    const instance = selectedInstance();
    if (instance && props.imageElement) {
      markDirty(instance.id);
      if (props.tool === 'erase') {
        instance.hasMask = maskHasPixels(instance.mask);
      }
      ensureKeypoint(
        instance,
        props.imageElement.width,
        props.imageElement.height,
      );
    }
    redraw();
    emit('edited');
    return;
  }
  if (wasDrag?.kind === 'rotate') {
    emit('edited');
    return;
  }
  if (wasDrag?.kind === 'pan') {
    // A pan-drag that never moved is a plain click: select the instance
    // under the cursor, so the mouse never has to travel to the Objects
    // panel. Real drags stay pure pans.
    const moved =
      Math.hypot(e.clientX - wasDrag.downX, e.clientY - wasDrag.downY) > 3;
    if (!moved && e.button === 0) {
      const pos = getMousePos(e);
      const hit = pos ? instanceAt(pos.x, pos.y) : null;
      if (hit !== null) {
        emit('select-instance', hit);
      }
    }
    return;
  }
  const pos = getMousePos(e);
  if (!pos || e.button !== 0) {
    return;
  }

  if (props.readOnly) {
    // Ready images route every mousedown into a pan drag, but a mouseup can
    // arrive without a prior mousedown — keep the mutating tools out.
    return;
  }

  if (props.tool === 'sam') {
    handleSamClick(pos, false);
    return;
  }
  if (props.tool === 'polygon') {
    const hit = polygonVertexHitTest(pos.x, pos.y);
    if (hit === 0 && polygonPoints.value.length >= 3) {
      commitPolygon();
      return;
    }
    if (hit >= 0) {
      polygonPoints.value.splice(hit, 1);
    } else {
      polygonPoints.value.push([pos.x, pos.y]);
    }
    emit('polygon-count-changed', polygonPoints.value.length);
    redraw();
    return;
  }
  if (props.tool === 'keypoint') {
    if (!props.imageElement) {
      return;
    }
    const { width, height } = props.imageElement;
    if (pos.x < 0 || pos.x > width || pos.y < 0 || pos.y > height) {
      return;
    }
    if (!activeInstance(true)) {
      return;
    }
    emit('keypoint-set', [pos.x, pos.y]);
    return;
  }
}

function handleMouseLeave() {
  // A stroke ending outside the canvas commits like one ending on mouseup:
  // the mask is already mutated, so skipping the finalization would leave
  // the edit unsaved until an unrelated later change.
  if (activeDrag?.kind === 'draw') {
    strokeStarted = false;
    const instance = selectedInstance();
    if (instance && props.imageElement) {
      markDirty(instance.id);
      if (props.tool === 'erase') {
        instance.hasMask = maskHasPixels(instance.mask);
      }
      ensureKeypoint(
        instance,
        props.imageElement.width,
        props.imageElement.height,
      );
    }
    emit('edited');
  }
  if (activeDrag?.kind === 'rotate') {
    // Same as a stroke: the angle is already mutated, commit the edit.
    emit('edited');
  }
  activeDrag = null;
  lastDrawPos = null;
  strokeStarted = false;
  brushCursor = null;
  hoveredPointIndex.value = -1;
  hoveredPolygonVertex.value = -1;
  polygonPreview.value = null;
  previewCanvas = null;
  lastPreviewPos = null;
  redraw();
}

function handleContextMenu(e: MouseEvent) {
  e.preventDefault();
  if (props.readOnly) {
    return;
  }
  if (props.tool === 'sam') {
    const pos = getMousePos(e);
    if (pos) {
      handleSamClick(pos, true);
    }
  }
}

// ---- rendering ----

function markDirty(id: number) {
  const entry = overlayCache.get(id);
  if (entry) {
    entry.dirty = true;
  }
}

function markAllDirty() {
  for (const entry of overlayCache.values()) {
    entry.dirty = true;
  }
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/** Per-instance blit opacity — prelabels render lighter than human
 * annotations; the selected instance renders strongest (per design). */
function instanceAlpha(instance: EditorInstance): number {
  // Selection wins: even an untouched prelabel renders strongest while
  // selected. Unselected objects fade far back so the edit target is
  // unmistakable; untouched prelabels faintest of all.
  if (instance.id === props.selectedId) {
    return 165 / 255;
  }
  return instance.source === 'prelabel' && !instance.touched
    ? 45 / 255
    : 65 / 255;
}

function overlayFor(instance: EditorInstance): HTMLCanvasElement | null {
  if (!props.imageElement) {
    return null;
  }
  const { width, height } = props.imageElement;
  let entry = overlayCache.get(instance.id);
  if (!entry) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    entry = { canvas, dirty: true, anchor: null, bbox: null };
    overlayCache.set(instance.id, entry);
  }
  if (entry.dirty) {
    const ctx = entry.canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    // Opaque tint; the instance-state alpha is applied at blit time so
    // selection changes never force a rebuild.
    const imageData = ctx.createImageData(width, height);
    const out = imageData.data;
    const [r, g, b] = hexToRgb(instanceColor(instance.id));
    const { mask } = instance;
    let anchor: [number, number] | null = null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < width * height; i++) {
      if (mask[i] > 0) {
        if (!anchor) {
          // Row-major scan: the first hit is the topmost mask pixel.
          anchor = [i % width, Math.floor(i / width)];
        }
        const px = i % width;
        const py = Math.floor(i / width);
        if (px < minX) {
          minX = px;
        }
        if (px > maxX) {
          maxX = px;
        }
        if (py < minY) {
          minY = py;
        }
        if (py > maxY) {
          maxY = py;
        }
        const o = i * 4;
        out[o] = r;
        out[o + 1] = g;
        out[o + 2] = b;
        out[o + 3] = 255;
      }
    }
    ctx.putImageData(imageData, 0, 0);
    entry.anchor = anchor;
    entry.bbox = anchor ? [minX, minY, maxX, maxY] : null;
    entry.dirty = false;
  }
  return entry.canvas;
}

/** Class name + ordinal, pinned above the topmost mask pixel. */
function drawInstanceLabel(
  ctx: CanvasRenderingContext2D,
  instance: EditorInstance,
  ordinal: number,
) {
  const anchor = overlayCache.get(instance.id)?.anchor;
  if (!anchor) {
    return;
  }
  const scale = 1 / zoom.value;
  const fontPx = 12 * scale;
  const padX = 5 * scale;
  const padY = 3 * scale;
  ctx.font = `600 ${fontPx}px Roboto, sans-serif`;
  const text = `${instance.categoryName || '?'} #${ordinal}`;
  const textWidth = ctx.measureText(text).width;
  const boxH = fontPx + padY * 2;
  const x = anchor[0];
  // Above the mask; flipped below the anchor when clipped at the image top.
  let y = anchor[1] - 4 * scale - boxH;
  if (y < 0) {
    y = anchor[1] + 4 * scale;
  }
  ctx.globalAlpha = instance.id === props.selectedId ? 0.95 : 0.75;
  ctx.fillStyle = instanceColor(instance.id);
  ctx.beginPath();
  ctx.roundRect(x, y, textWidth + padX * 2, boxH, 4 * scale);
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x + padX, y + boxH / 2);
}

function drawKeypoint(
  ctx: CanvasRenderingContext2D,
  instance: EditorInstance,
  selected: boolean,
) {
  if (!instance.keypoint) {
    return;
  }
  const [x, y] = instance.keypoint;
  const r = (selected ? 7 : 5) / zoom.value;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.strokeStyle = '#DF4C85';
  ctx.lineWidth = 2.5 / zoom.value;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, 1.5 / zoom.value, 0, Math.PI * 2);
  ctx.fillStyle = '#DF4C85';
  ctx.fill();

  if (props.rotationEnabled && instance.angleDeg !== null) {
    // Angle around Z from top view; zero points along the image y-axis.
    const rad = (instance.angleDeg * Math.PI) / 180;
    const ux = Math.sin(rad);
    const uy = -Math.cos(rad);
    // Arrow ends just inside the object's edge in the direction it points:
    // cast from the pick-point to the mask-bbox boundary, in image space,
    // so it tracks the object's extent at any angle and zoom.
    const bbox = overlayCache.get(instance.id)?.bbox;
    const forward = bbox ? rayToBoxEdge(x, y, ux, uy, bbox) : null;
    const len = forward !== null ? forward * 0.95 : 42 / zoom.value;
    if (selected) {
      // Dashed alignment guide poking equally just past the object's edge
      // on both sides — for checking the angle against part features.
      const backward = bbox ? rayToBoxEdge(x, y, -ux, -uy, bbox) : null;
      const ahead = (forward ?? len) * 1.15;
      const behind = (backward ?? len) * 1.15;
      ctx.save();
      ctx.setLineDash([6 / zoom.value, 6 / zoom.value]);
      ctx.strokeStyle = '#DF4C85';
      ctx.lineWidth = 1.5 / zoom.value;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.moveTo(x - ux * behind, y - uy * behind);
      ctx.lineTo(x + ux * ahead, y + uy * ahead);
      ctx.stroke();
      ctx.restore();
    }
    const dx = ux * len;
    const dy = uy * len;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + dx, y + dy);
    ctx.strokeStyle = '#DF4C85';
    ctx.lineWidth = 2.5 / zoom.value;
    ctx.stroke();
    const head = Math.max(6 / zoom.value, len * 0.12);
    const angle = Math.atan2(dy, dx);
    ctx.beginPath();
    ctx.moveTo(x + dx, y + dy);
    ctx.lineTo(
      x + dx - head * Math.cos(angle - Math.PI / 6),
      y + dy - head * Math.sin(angle - Math.PI / 6),
    );
    ctx.moveTo(x + dx, y + dy);
    ctx.lineTo(
      x + dx - head * Math.cos(angle + Math.PI / 6),
      y + dy - head * Math.sin(angle + Math.PI / 6),
    );
    ctx.stroke();
  }
}

/** Distance from (x, y) to the box boundary along (ux, uy); null when the
 * point lies outside the box. */
function rayToBoxEdge(
  x: number,
  y: number,
  ux: number,
  uy: number,
  [minX, minY, maxX, maxY]: [number, number, number, number],
): number | null {
  if (x < minX || x > maxX || y < minY || y > maxY) {
    return null;
  }
  const tx = ux > 0 ? (maxX - x) / ux : ux < 0 ? (minX - x) / ux : Infinity;
  const ty = uy > 0 ? (maxY - y) / uy : uy < 0 ? (minY - y) / uy : Infinity;
  const t = Math.min(tx, ty);
  return Number.isFinite(t) ? t : null;
}

function drawSamPoints(ctx: CanvasRenderingContext2D) {
  if (!props.imageElement) {
    return;
  }
  const { width, height } = props.imageElement;
  const baseR = 7 / zoom.value;
  for (let i = 0; i < samPoints.value.length; i++) {
    const p = samPoints.value[i];
    const hovered = i === hoveredPointIndex.value;
    const r = hovered ? baseR * 1.4 : baseR;
    const cx = p.point[0] * width;
    const cy = p.point[1] * height;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = hovered ? '#6b7280' : p.label === 1 ? '#22c55e' : '#ef4444';
    ctx.fill();
    ctx.lineWidth = 2 / zoom.value;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
  }
}

function drawPolygonDraft(ctx: CanvasRenderingContext2D) {
  const verts = polygonPoints.value;
  const preview = polygonPreview.value;
  if (verts.length === 0 && !preview) {
    return;
  }
  const accent = '#83DED9';
  const lw = 2 / zoom.value;
  const dash = 6 / zoom.value;

  if (verts.length >= 2) {
    ctx.beginPath();
    ctx.moveTo(verts[0][0], verts[0][1]);
    for (let i = 1; i < verts.length; i++) {
      ctx.lineTo(verts[i][0], verts[i][1]);
    }
    ctx.lineWidth = lw;
    ctx.strokeStyle = accent;
    ctx.setLineDash([]);
    ctx.stroke();
  }
  if (preview && verts.length >= 1) {
    ctx.setLineDash([dash, dash]);
    ctx.lineWidth = lw;
    ctx.strokeStyle = accent;
    ctx.beginPath();
    ctx.moveTo(verts[verts.length - 1][0], verts[verts.length - 1][1]);
    ctx.lineTo(preview[0], preview[1]);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  const baseR = 5 / zoom.value;
  for (let i = 0; i < verts.length; i++) {
    const hovered = i === hoveredPolygonVertex.value;
    ctx.beginPath();
    ctx.arc(
      verts[i][0],
      verts[i][1],
      hovered ? baseR * 1.5 : baseR,
      0,
      Math.PI * 2,
    );
    // First vertex doubles as the close target.
    ctx.fillStyle = i === 0 ? '#FCF9F7' : accent;
    ctx.fill();
    ctx.lineWidth = 1.5 / zoom.value;
    ctx.strokeStyle = i === 0 ? accent : '#ffffff';
    ctx.stroke();
  }
}

function redraw() {
  const canvas = canvasRef.value;
  const image = props.imageElement;
  if (!canvas) {
    return;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (!image) {
    return;
  }
  ctx.setTransform(zoom.value, 0, 0, zoom.value, pan.x, pan.y);
  ctx.drawImage(image, 0, 0);

  for (const instance of props.instances) {
    const overlay = overlayFor(instance);
    if (overlay) {
      // While an object is selected, the others render desaturated on top
      // of their lower alpha, so the object being worked on is unmistakable.
      if (props.selectedId !== null && instance.id !== props.selectedId) {
        ctx.filter = 'grayscale(0.75)';
      }
      ctx.globalAlpha = instanceAlpha(instance);
      ctx.drawImage(overlay, 0, 0);
      ctx.globalAlpha = 1;
      ctx.filter = 'none';
    }
  }
  if (previewCanvas && props.tool === 'sam') {
    // Lighter than a committed mask — it is only a suggestion.
    ctx.globalAlpha = 100 / 255;
    ctx.drawImage(previewCanvas, 0, 0);
    ctx.globalAlpha = 1;
  }
  for (let i = 0; i < props.instances.length; i++) {
    drawInstanceLabel(ctx, props.instances[i], i + 1);
  }
  for (const instance of props.instances) {
    drawKeypoint(ctx, instance, instance.id === props.selectedId);
  }
  if (props.tool === 'sam') {
    drawSamPoints(ctx);
  }
  if (props.tool === 'polygon') {
    drawPolygonDraft(ctx);
  }
  if ((props.tool === 'brush' || props.tool === 'erase') && brushCursor) {
    ctx.beginPath();
    ctx.arc(
      // Same geometry as the drawAtPosition stamp (pixel-index disk rendered
      // half a pixel down-right and half a pixel larger), so the ring
      // predicts the painted disc exactly.
      Math.round(brushCursor.x) + 0.5,
      Math.round(brushCursor.y) + 0.5,
      Math.max(1, Math.round(props.brushSize / zoom.value)) + 0.5,
      0,
      Math.PI * 2,
    );
    ctx.strokeStyle = props.tool === 'erase' ? '#ef4444' : '#FCF9F7';
    ctx.lineWidth = 1.5 / zoom.value;
    ctx.stroke();
  }
}

function clearSamPoints() {
  samPoints.value = [];
  redraw();
}

defineExpose({
  zoomIn,
  zoomOut,
  zoomToFit,
  redraw,
  markAllDirty,
  markDirty,
  clearSamPoints,
  cancelDrafts,
  setPreviewMask,
});
</script>

<template>
  <div ref="containerRef" class="annotation-canvas-container">
    <canvas
      ref="canvasRef"
      :class="`cursor-${tool}`"
      @wheel="handleWheel"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseLeave"
      @contextmenu="handleContextMenu"
    ></canvas>
  </div>
</template>

<style scoped>
.annotation-canvas-container {
  position: absolute;
  inset: 0;
  overflow: hidden;
}
canvas {
  display: block;
}
.cursor-sam,
.cursor-polygon,
.cursor-keypoint {
  cursor: crosshair;
}
.cursor-brush,
.cursor-erase {
  cursor: none;
}
.cursor-pan {
  cursor: grab;
}
</style>
